import { useScrollDepth } from "@/hooks";
import {
  Footer,
  Header,
  WhatsAppFloatButton,
} from "@/pages/landingPage/components";
import { PRESS_ARTICLES } from "@/pages/landingPage/constants/pressArticles";
import { t } from "@/context/language";
import { ArrowUp, ArrowUpRight } from "lucide-react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://fidexa.com.br";
const PATH = "/newsroom";
// Sem o sufixo "| Fidexa" aqui: o <Helmet titleTemplate="%s | Fidexa"> global
// (src/App.tsx) já concatena o sufixo no <title> renderizado.
const TITLE = "Newsroom";
const OG_TITLE = `${TITLE} | Fidexa`;
const DESCRIPTION =
  "A Fidexa recuperou R$ 39,34 milhões para lojistas do varejo em menos de um ano. Conheça a empresa, os números e o que a imprensa está publicando.";

const MERRIWEATHER = { fontFamily: "var(--font-merriweather)" } as const;

// Foto original (4488x2953) reduzida para 1600px de largura — a coluna de
// leitura tem 768px, então 1600 já cobre telas 2x sem peso desnecessário.
const COVER = {
  src: "/landing-page/newsroom/fundadores-fidexa.jpg",
  width: 1600,
  height: 1052,
  alt: "Os quatro fundadores da Fidexa lado a lado, de camisa preta com o logo da empresa",
  caption: "Os fundadores da Fidexa.",
};

const DATELINE = "Setembro de 2026";

const FOUNDERS = "Bruno Reis, Hudson Ribeiro, Leonardo Azevedo e André Menezes";

const PRESS_EMAIL = "imprensa@fidexa.com.br";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Início", item: `${SITE_URL}/` },
    {
      "@type": "ListItem",
      position: 2,
      name: "Newsroom",
      item: `${SITE_URL}${PATH}`,
    },
  ],
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Fidexa",
  url: SITE_URL,
  logo: `${SITE_URL}/og-image.png`,
  foundingDate: "2025-10",
  description:
    "Plataforma de gestão e recuperação de crediário para o varejo brasileiro, com cobrança automatizada via WhatsApp.",
  founder: [
    { "@type": "Person", name: "Bruno Reis" },
    { "@type": "Person", name: "Hudson Ribeiro" },
    { "@type": "Person", name: "Leonardo Azevedo" },
    { "@type": "Person", name: "André Menezes" },
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "press",
    email: PRESS_EMAIL,
    availableLanguage: "Portuguese",
  },
};

// Extrai o domínio exibido abaixo de cada manchete (ex.: "baguete.com.br").
const domainOf = (url: string): string =>
  new URL(url).hostname.replace(/^www\./, "");

export function Newsroom() {
  useScrollDepth();

  // Mesma fonte de verdade da seção de imprensa da home: texto nas traduções,
  // link e logo no mapa de assets. Casado pelo veículo, não pela posição.
  const articles = t.press.items
    .map(item => {
      const asset = PRESS_ARTICLES[item.outlet];
      return asset ? { ...item, ...asset } : null;
    })
    .filter(article => article !== null);

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={`${SITE_URL}${PATH}`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={OG_TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}${PATH}`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={OG_TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen max-w-screen overflow-hidden bg-white">
        <Header />

        {/* Capa em largura total, logo abaixo do Header (que é `fixed`, daí o
            padding-top). O recorte é definido por proporção, não por altura
            fixa: assim a fatia visível da foto é a mesma em qualquer largura
            de tela. `object-top` mantém os rostos; o `max-h` evita que a capa
            tome a tela inteira em monitores largos. */}
        <figure className="pt-16 lg:pt-20">
          <img
            src={COVER.src}
            alt={COVER.alt}
            width={COVER.width}
            height={COVER.height}
            fetchPriority="high"
            className="aspect-[4/3] max-h-[70vh] w-full object-cover object-top sm:aspect-[16/9]"
          />
          <figcaption className="mx-auto w-full max-w-3xl px-6 pt-3 text-sm text-gray-500">
            {COVER.caption}
          </figcaption>
        </figure>

        <main className="mx-auto w-full max-w-3xl px-6 pb-20 lg:pb-28">
          <header className="mt-12 flex flex-col gap-4">
            <div className="flex items-center gap-3 text-xs tracking-widest text-gray-500 uppercase">
              <span>Newsroom</span>
              <span aria-hidden="true">·</span>
              <span>{DATELINE}</span>
            </div>

            <h1
              style={MERRIWEATHER}
              className="text-3xl leading-tight font-bold text-black sm:text-4xl lg:text-5xl"
            >
              R$ 39,4 milhões de volta no caixa do varejo
            </h1>
          </header>

          <article className="mt-10 flex flex-col gap-6 text-base leading-relaxed text-gray-700 sm:text-lg">
            <p>
              No varejo brasileiro, o crediário próprio não é um detalhe de
              loja pequena: é o motor de redes regionais inteiras, que somam
              dezenas de unidades e movimentam milhões por mês financiando a
              própria venda. Em redes de calçados, roupas e móveis, é o
              crediário que responde pela maior parte do faturamento — ele
              permite parcelar uma compra em quatro ou seis vezes sem cartão e
              sem banco. Quem analisa o crédito, aprova o limite e assume o
              risco é a própria rede, com sistema de análise, política de
              crédito e carnê dentro do ERP.
            </p>

            <p>
              Financiar a própria venda significa carregar o risco dela — e o
              risco aparece quando a parcela não volta. Na escala de uma rede,
              isso não é um cliente inadimplente: é uma carteira com milhares
              de parcelas em atraso espalhadas por dezenas de unidades. Cada
              uma, sozinha, é pequena demais para justificar escritório de
              cobrança ou protesto; muitas são de R$ 50. Somadas, são milhões
              parados fora do caixa.
            </p>

            <p>
              O time financeiro sabe exatamente quem deve o quê — o relatório
              está no ERP. O que não existe é braço para cobrar: lembrar,
              escrever, negociar, registrar a promessa de pagamento e repetir
              isso milhares de vezes por mês, em todas as lojas, sem
              constranger o cliente que a rede quer de volta no mês seguinte.
              Contratar gente na mesma proporção da carteira não fecha a conta,
              e é por isso que a maior parte desse dinheiro simplesmente não
              é cobrada.
            </p>

            <p>
              A Fidexa nasceu em outubro de 2025 para resolver esse trabalho. A
              plataforma se conecta à base de crediário da loja, organiza a
              carteira de inadimplentes e coloca uma régua de cobrança para
              rodar no WhatsApp: antes do vencimento, no vencimento e ao longo
              do atraso. A inteligência artificial ajusta tom e frequência das
              mensagens de acordo com o comportamento de cada devedor — quem
              costuma pagar recebe um lembrete; quem some há meses recebe uma
              abordagem diferente. Hoje são cerca de 15 mil mensagens por dia.
            </p>

            <p>
              O resultado é simples de medir. Em menos de um ano de operação, a
              Fidexa recuperou{" "}
              <strong className="font-semibold text-black">
                R$ 39,34 milhões
              </strong>{" "}
              para lojistas do varejo — R$ 34 milhões em parcelas e R$ 5,34
              milhões em juros —, com taxa média de recuperação de 20% das
              carteiras conectadas. São 249 empresas usando a plataforma. A
              empresa atingiu breakeven com capital próprio e gera caixa.
            </p>

            <p>
              A etapa seguinte é fechar o ciclo dentro da própria conversa. Com
              o Fidexa Pay, o cliente negocia o débito e paga via Pix sem sair
              do WhatsApp, e a baixa volta para o sistema da loja. A integração
              com o ERP da TOTVS Linx foi a primeira a entrar no ar — a conexão
              com outros ERPs do varejo está em andamento —, unindo gestão de
              recebíveis, negociação e pagamento em um painel só, com visão
              consolidada de todas as unidades da rede.
            </p>

            <p>
              A Fidexa foi fundada por Bruno Reis, Hudson Ribeiro e Leonardo
              Azevedo. Em 2026, André Menezes entrou como quarto sócio: ele
              cofundou a TiNDLE (Next Gen Foods), que levantou US$ 130 milhões
              em rodadas internacionais — incluindo uma Série A de US$ 100
              milhões com participação do Temasek.
            </p>

            <p>
              A meta para o fim de 2026 é dobrar a base de clientes e ampliar as
              integrações com ERPs do varejo. O objetivo por trás dela continua
              o mesmo do primeiro dia: transformar dívida parada em caixa para o
              lojista, sem que ele precise contratar mais uma pessoa para o
              financeiro — e sem queimar a relação com o cliente que volta à
              loja no mês seguinte.
            </p>

            <p className="pt-2 text-gray-500">
              — {FOUNDERS}, fundadores da Fidexa.
            </p>
          </article>

          <section className="mt-16 border-t border-gray-200 pt-10">
            <h2 className="text-xs tracking-widest text-gray-500 uppercase">
              Na imprensa
            </h2>

            <ul className="mt-2 flex flex-col">
              {articles.map(article => (
                <li key={article.url} className="border-b border-gray-200">
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="focus-visible:outline-primary group flex cursor-pointer items-start gap-4 py-6 focus-visible:outline-2 focus-visible:outline-offset-2 sm:gap-6"
                  >
                    <span className="flex size-14 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 p-2.5">
                      <img
                        src={article.logo}
                        alt={article.logoAlt}
                        loading="lazy"
                        className="max-h-full w-auto max-w-full object-contain opacity-70 brightness-0 transition group-hover:opacity-100"
                      />
                    </span>

                    <span className="flex flex-1 flex-col gap-1">
                      <span
                        style={MERRIWEATHER}
                        className="text-lg leading-snug font-bold text-black sm:text-xl"
                      >
                        {article.title}
                      </span>
                      <span className="text-sm text-gray-500">
                        {domainOf(article.url)}
                      </span>
                    </span>

                    <ArrowUpRight
                      className="mt-1 size-5 shrink-0 text-gray-400 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gray-600"
                      aria-hidden="true"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12 border-t border-gray-200 pt-10">
            <h2 className="text-xs tracking-widest text-gray-500 uppercase">
              Contato para imprensa
            </h2>

            <p className="mt-3 text-base text-gray-700 sm:text-lg">
              Para entrevistas, dados de mercado ou material institucional,
              escreva para{" "}
              <a
                href={`mailto:${PRESS_EMAIL}`}
                className="focus-visible:outline-primary text-primary cursor-pointer font-medium underline decoration-transparent underline-offset-4 transition hover:decoration-current focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                {PRESS_EMAIL}
              </a>
              .
            </p>

            <button
              type="button"
              onClick={() =>
                window.scrollTo({
                  top: 0,
                  behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
                    .matches
                    ? "auto"
                    : "smooth",
                })
              }
              className="focus-visible:outline-primary group mt-8 flex cursor-pointer items-center gap-2 text-xs tracking-widest text-gray-500 uppercase transition-colors hover:text-black focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              Voltar ao início
              <ArrowUp
                className="size-4 transition group-hover:-translate-y-0.5"
                aria-hidden="true"
              />
            </button>
          </section>
        </main>

        <Footer />
        <WhatsAppFloatButton />
      </div>
    </>
  );
}
